<script setup lang="ts">
import VueTurnstile from 'vue-turnstile';

type Props = {
  isPositionAbsolute?: boolean;
  fieldHasError?: boolean;
};

const { isPositionAbsolute = true, fieldHasError = false } =
  defineProps<Props>();

const { $captcha } = useNuxtApp();
const { t } = useTexts();

const {
  turnstileAlwaysShowEnv,
  isRecaptchaEnabled,
  isTurnstileEnabled,
  turnstileSiteKey,
  recaptchaSiteKey,
  captchaStyle,
  isInvisibleCaptcha,
} = useCaptcha();

// Turnstile

const turnstileCompRef = ref();
const turnstileCompKey = ref('turnstileCompKey');
const turnstileToken = ref<string>($captcha.initialTokenValue.value);

const turnstileTheme = computed<'light' | 'dark'>(() => {
  if (captchaStyle.value === 'light') {
    return 'light';
  }

  return 'dark';
});

// Recaptcha

const recaptchaToken = ref('');
const recaptchaLogo = getAssetsUrlForCDN('/assets/images/recaptcha.png');

// Captcha

const isCaptchaLoaded = ref<boolean>(false);
const captchaManualError = ref(false);
const currentTrying = ref('T');

const finalCaptchaTokenValue = computed<string>(() => {
  if (isTurnstileEnabled.value && turnstileToken.value) {
    if (turnstileAlwaysShowEnv) {
      return `t2:${turnstileToken.value}`;
    }

    return `t1:${turnstileToken.value}`;
  } else if (isRecaptchaEnabled.value && recaptchaToken.value) {
    return `r1:${recaptchaToken.value}`;
  }

  return '';
});

const clickToTryText = computed(() => {
  return t('auth.login.try_again');
});

const validatingText = computed(() => {
  const showType = isRecaptchaEnabled.value && isTurnstileEnabled.value;

  return `
    ${t('auth.login.validating_captcha')}
    ${showType ? `(${currentTrying.value})` : ''}
  `;
});

const onTurnstileErrorFunction = (err: any) => {
  console.debug('turnstileError', err);

  if (isRecaptchaEnabled.value) {
    executeRecaptcha();
  } else {
    setTimeout(() => {
      captchaResetFunction();
    }, 2000);
  }

  return false;
};

const onTurnstileUnsupportedFunction = (err: any) => {
  console.debug('turnstileUnsupported', err);

  if (isRecaptchaEnabled.value) {
    executeRecaptcha();
  }

  return false;
};

let captchaTimeout: ReturnType<typeof setTimeout> | null = null;

function initCaptchaTimoutOrRecaptcha() {
  if (captchaTimeout) {
    clearTimeout(captchaTimeout);
  }

  if (finalCaptchaTokenValue.value || isCaptchaLoaded.value) {
    return;
  }

  if (isRecaptchaEnabled.value && !isTurnstileEnabled.value) {
    executeRecaptcha();
  } else {
    captchaTimeout = setTimeout(() => {
      if (isRecaptchaEnabled.value) {
        executeRecaptcha();
      }
    }, 9000);
  }
}

function captchaResetFunction() {
  const dateNow = new Date();
  const newKey = dateNow.toString() + '_captcha';

  captchaManualError.value = false;
  turnstileToken.value = '';
  turnstileCompKey.value = newKey;
  recaptchaToken.value = '';
  currentTrying.value = 'T';

  // O ref pode ser null quando estamos no fluxo de recaptcha e o
  // <vue-turnstile> não está renderizado (v-else). Optional chaining
  // evita crash quando o reset é agendado por setTimeout.
  turnstileCompRef.value?.reset();

  initCaptchaTimoutOrRecaptcha();
}

function $resetAllCaptcha() {
  captchaResetFunction();
}

const loadRecaptchaScript = () => {
  if (!isRecaptchaEnabled.value) {
    return;
  }

  return new Promise((resolve: any, reject: any) => {
    if (document.getElementById('recaptcha-script')) {
      return resolve();
    }

    const script = document.createElement('script');

    script.id = 'recaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey.value}`;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const executeRecaptcha = async () => {
  if (finalCaptchaTokenValue.value) {
    return;
  }

  if (!isRecaptchaEnabled.value) {
    return;
  }

  console.debug('🆘 executeRecaptcha');

  currentTrying.value = 'R';

  try {
    await loadRecaptchaScript();

    // @ts-ignore
    grecaptcha.ready(() => {
      // @ts-ignore
      grecaptcha
        .execute(recaptchaSiteKey.value, { action: 'submit' })
        .then((token: any) => {
          recaptchaToken.value = token;
        });
    });

    setTimeout(() => {
      if (!finalCaptchaTokenValue.value) {
        if (isTurnstileEnabled.value) {
          captchaResetFunction();
        } else {
          executeRecaptcha();
        }
      }
    }, 12000);
  } catch (error) {
    captchaManualError.value = true;
    console.error('Error reCAPTCHA:', error);
  }
};

watch(
  () => finalCaptchaTokenValue.value,
  () => {
    if (!finalCaptchaTokenValue.value) {
      isCaptchaLoaded.value = !!finalCaptchaTokenValue.value;
    } else {
      setTimeout(() => {
        isCaptchaLoaded.value = !!finalCaptchaTokenValue.value;
      }, 2000);
    }
  },
);

onMounted(() => {
  initCaptchaTimoutOrRecaptcha();
});

onActivated(() => {
  initCaptchaTimoutOrRecaptcha();
});

// Erros lançados pelo widget do Cloudflare Turnstile (ex.: container nulo
// em race do DOM) não devem derrubar a página. Captura e cai no reCAPTCHA
// se disponível. O Sentry filtra TurnstileError no plugin global.
onErrorCaptured((err: any) => {
  const message = err?.message || String(err);

  if (message.includes('Turnstile') || message.includes('turnstile')) {
    if (isRecaptchaEnabled.value) {
      executeRecaptcha();
    }

    return false;
  }

  return undefined;
});

defineExpose({
  finalCaptchaTokenValue,
  $resetAllCaptcha,
});
</script>

<template>
  <ClientOnly>
    <div
      :class="[
        $style.antiRobotContainer,
        { [$style.isInvisible]: isInvisibleCaptcha },
        { [$style.fieldHasError]: fieldHasError },
        $style[turnstileTheme],
      ]"
    >
      <div
        v-if="currentTrying === 'R'"
        :class="$style.hcaptchaComponent"
      >
        <div :class="[$style.recaptchaContainer, $style[turnstileTheme]]">
          <div :class="$style.recaptchaState">
            <template v-if="!finalCaptchaTokenValue">
              <div :class="$style.recaptchaIcon">
                <div :class="$style.recaptchaSpinner"></div>
              </div>
              <span>{{ validatingText }}</span>
            </template>
            <template v-else>
              <div :class="$style.recaptchaIcon">
                <NuxtIcon
                  :class="$style.validated"
                  name="check"
                />
              </div>
              <span>Captcha validado!</span>
            </template>
          </div>
          <div
            :class="$style.recaptchaLogo"
            :style="`background-image: url(${recaptchaLogo})`"
          />
        </div>
      </div>

      <div
        v-else
        :class="$style.turnstileComponent"
      >
        <vue-turnstile
          :key="turnstileCompKey"
          ref="turnstileCompRef"
          v-model="turnstileToken"
          :appearance="turnstileAlwaysShowEnv ? 'always' : 'interaction-only'"
          render-on-mount
          :site-key="turnstileSiteKey"
          :theme="turnstileTheme"
          @error="onTurnstileErrorFunction"
          @unsupported="onTurnstileUnsupportedFunction"
        />
      </div>
    </div>

    <div
      v-if="!isCaptchaLoaded && isInvisibleCaptcha"
      :class="[
        $style.captchaLoading,
        { [$style.fieldHasError]: fieldHasError },
        { [$style.hasError]: captchaManualError },
        { [$style.isPositionAbsolute]: isPositionAbsolute },
      ]"
      @click="captchaResetFunction"
    >
      <NuxtIcon
        v-if="!finalCaptchaTokenValue && !captchaManualError"
        :class="[$style.captchaLoadingIcon, 'animate-spin']"
        filled
        name="loading"
      />
      <NuxtIcon
        v-else-if="!captchaManualError"
        :class="[$style.captchaLoadingIcon, 'text-success']"
        name="check"
      />
      <NuxtIcon
        v-else-if="captchaManualError"
        :class="[$style.captchaLoadingIcon, 'text-warning']"
        name="sync"
      />
      {{ captchaManualError ? clickToTryText : validatingText }}
    </div>
  </ClientOnly>
</template>

<style module lang="postcss">
.antiRobotContainer {
  @apply flex justify-center items-center mx-auto px-1 whitespace-nowrap bg-transparent rounded-lg w-full h-[64px] contrast-[110%];

  &.isInvisible {
    @apply absolute opacity-0 -z-10 pointer-events-none;
  }

  &.light {
    @apply bg-[#ffffff];
  }

  &.dark {
    @apply bg-[#232323];
  }

  &.fieldHasError {
    @apply border-error;
    animation: shake 200ms ease-in-out;
  }

  .turnstileComponent {
    @apply w-[295px] h-[60px] overflow-hidden flex items-center justify-center;

    div {
      @apply align-middle flex items-center justify-center;

      iframe {
        @apply align-top;
      }
    }
  }

  .hcaptchaComponent {
    @apply w-full h-[60px] overflow-hidden flex items-center justify-center px-4;

    .recaptchaContainer {
      @apply w-full m-auto flex items-center justify-between;

      &.light {
        @apply text-black;
      }

      &.dark {
        @apply text-white;
      }

      .recaptchaState {
        @apply flex items-center justify-center gap-4;

        span {
          @apply text-xs leading-3 font-semibold;
        }

        .recaptchaIcon {
          @apply w-6 h-6 flex items-center justify-center overflow-hidden rounded-full;

          .validated {
            @apply text-success text-lg;
          }

          .recaptchaSpinner {
            @apply border-4 border-solid border-[#f3f3f3] border-t-[#4285f4] rounded-full w-6 h-6;

            animation: spin 1s linear infinite;
          }
        }
      }

      .recaptchaLogo {
        @apply bg-no-repeat bg-contain w-12 h-12;
      }
    }
  }
}

.captchaLoading {
  @apply flex justify-center items-center gap-2 text-[.65rem] sm:text-[.7rem] leading-3 text-texts/60 py-2.5 text-center;

  &.hasError {
    @apply text-auth-texts/40 cursor-pointer;
  }

  &.fieldHasError {
    @apply border-error;
    animation: shake 200ms ease-in-out;
  }

  &.isPositionAbsolute {
    @apply absolute top-full left-0 right-0 py-1;
  }

  .captchaLoadingIcon {
    @apply text-texts w-2.5 h-2.5 sm:w-3 sm:h-3 p-0 m-0 flex items-center justify-center leading-none;

    svg {
      @apply w-2.5 h-2.5 sm:w-3 sm:h-3 !important;
    }
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes shake {
  0% {
    transform: translateX(-3px);
  }
  25% {
    transform: translateX(3px);
  }
  50% {
    transform: translateX(-3px);
  }
  75% {
    transform: translateX(3px);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
